import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

const easeCurve = [0.16, 1, 0.3, 1];
const VIEWPORT = { once: true, margin: "-80px" };
const VISIBLE = { opacity: 1, y: 0 };

// Crawlers don't play animations — always render fully static for them
const IS_BOT =
  typeof navigator !== "undefined" &&
  /bot|googlebot|bingbot|crawler|spider|slurp|lighthouse|inspectiontool|headless/i.test(navigator.userAgent);

// Content is fully visible by default; scroll fade-in applies only to
// elements below the fold on mount (progressive enhancement).
const useBelowFold = () => {
  const ref = useRef(null);
  const [belowFold, setBelowFold] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && el.getBoundingClientRect().top > window.innerHeight) {
      setBelowFold(true);
    }
  }, []);
  return [ref, belowFold];
};

export const Reveal = ({ children, delay = 0, y = 40, className = "", ...rest }) => {
  const reduce = useReducedMotion();
  const [ref, belowFold] = useBelowFold();
  const hidden = useMemo(() => ({ opacity: 0, y }), [y]);
  const transition = useMemo(() => ({ duration: 0.8, delay, ease: easeCurve }), [delay]);

  if (reduce || IS_BOT) {
    return (
      <div className={className} {...rest}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={belowFold ? hidden : false}
      whileInView={VISIBLE}
      viewport={VIEWPORT}
      transition={transition}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

export const RevealStagger = ({ children, className = "", stagger = 0.12 }) => {
  const reduce = useReducedMotion();
  const [ref, belowFold] = useBelowFold();
  const variants = useMemo(
    () => ({ hidden: {}, show: { transition: { staggerChildren: stagger } } }),
    [stagger]
  );

  if (reduce || IS_BOT || !belowFold) {
    return (
      <div ref={ref} className={className}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={VIEWPORT}
      variants={variants}
    >
      {children}
    </motion.div>
  );
};

export const StaggerItem = ({ children, className = "", y = 40 }) => {
  const variants = useMemo(
    () => ({
      hidden: { opacity: 0, y },
      show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: easeCurve } },
    }),
    [y]
  );
  return (
    <motion.div className={className} variants={variants}>
      {children}
    </motion.div>
  );
};
